import { useEffect, useState } from 'react';
import { getMpDiagnostics, getPizasSettings, updatePizasSettings } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { CreditCard, Printer } from 'lucide-react';
import ReceiptTicketImageField from '../components/ReceiptTicketImageField';

export default function AdminPizasStore() {
  const [form, setForm] = useState(null);
  const [mpDiag, setMpDiag] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([getPizasSettings(), getMpDiagnostics().catch(() => null)])
      .then(([pizas, diag]) => {
        setForm(pizas);
        setMpDiag(diag);
      })
      .catch(() => toast.error('No se pudo cargar la configuración'));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await updatePizasSettings(form);
      const diag = await getMpDiagnostics().catch(() => null);
      setMpDiag(diag);
      toast.success('Configuración guardada');
    } catch {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const waStoreVars =
    '{store_name}, {brand_name}, {pizzeria_name} — mismo valor: el nombre de tu pizzería';

  if (!form) return <div>Cargando…</div>;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Configuración de tienda</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 max-w-xl">
        <div className="rounded-lg border-2 border-primary/30 bg-primary/5 p-4 space-y-3">
          <p className="text-sm font-semibold">Nombre de la pizzería</p>
          <p className="text-sm text-muted-foreground">
            Se usa en tickets del bot, saludo y plantillas de WhatsApp. En cualquier mensaje escribe{' '}
            <code className="text-xs">{'{store_name}'}</code> y al enviar se sustituye por este nombre.
            Si lo cambias aquí, los mensajes que usen la variable muestran el nombre nuevo al instante.
          </p>
          <div>
            <Label htmlFor="wa-brand-name">Nombre comercial</Label>
            <Input
              id="wa-brand-name"
              value={form.wa_brand_name || ''}
              onChange={(e) => set('wa_brand_name', e.target.value)}
              className="h-11 mt-1"
              placeholder="Pizza Lover Club"
            />
          </div>
          <div>
            <Label htmlFor="wa-menu-greeting">Saludo inicial del bot (WhatsApp)</Label>
            <Textarea
              id="wa-menu-greeting"
              rows={3}
              value={form.wa_menu_greeting_text || ''}
              onChange={(e) => set('wa_menu_greeting_text', e.target.value)}
              className="mt-1"
              placeholder="¡Hola! Bienvenido a *{store_name}* 🍕"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Variables: {waStoreVars}. Solo el texto de bienvenida; el bot añade la leyenda de tiempo
              estimado y la opción «Ver menú y ordenar». El carrito aparece solo si el cliente ya agregó
              productos.
            </p>
          </div>
        </div>
        <div className="rounded-lg border-2 border-primary/30 bg-primary/5 p-4 space-y-3">
          <p className="text-sm font-semibold">URL pública del sitio (enlaces de pago)</p>
          <p className="text-sm text-muted-foreground">
            Los links de pago por WhatsApp usan esta URL + <code className="text-xs">/pagar/ID</code>.
            En local suele ser <strong>http://127.0.0.1:3080</strong>; en producción tu dominio HTTPS.
          </p>
          <div>
            <Label htmlFor="public-base-url-pizas-store">URL pública base</Label>
            <Input
              id="public-base-url-pizas-store"
              value={form.public_base_url || ''}
              onChange={(e) => set('public_base_url', e.target.value)}
              className="h-11 mt-1"
              placeholder="https://tudominio.com"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Ejemplo de link: {((form.public_base_url || 'https://tudominio.com').replace(/\/$/, ''))}/pagar/abc123
          </p>
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
            No uses un link de Mercado Pago aquí. Debe ser la URL de tu sitio Pizas (donde abre /pagar/…).
          </p>
        </div>
        <p className="text-sm text-muted-foreground">
          La columna <strong>Zona</strong> en pedidos compara la ubicación del cliente con estas
          coordenadas (fórmula Haversine) y el <strong>radio de entrega</strong>. Si ves muchos km de
          más, revisa que latitud y longitud sean las de tu pizzería (en Google Maps: clic derecho en
          el local → copiar coordenadas).
        </p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Latitud tienda</Label>
            <Input
              type="number"
              step="any"
              value={form.store_latitude}
              onChange={(e) => set('store_latitude', parseFloat(e.target.value))}
            />
          </div>
          <div>
            <Label>Longitud tienda</Label>
            <Input
              type="number"
              step="any"
              value={form.store_longitude}
              onChange={(e) => set('store_longitude', parseFloat(e.target.value))}
            />
          </div>
        </div>
        <div>
          <Label>Radio entrega (km)</Label>
          <Input
            type="number"
            value={form.max_delivery_radius_km}
            onChange={(e) => set('max_delivery_radius_km', parseFloat(e.target.value))}
          />
        </div>
        <div>
          <Label>Minutos por pizza (cola)</Label>
          <Input
            type="number"
            value={form.default_pizza_prep_minutes}
            onChange={(e) => set('default_pizza_prep_minutes', parseInt(e.target.value, 10))}
          />
        </div>
        <div className="rounded-lg border-2 border-emerald-600/30 p-4 space-y-4">
          <div className="flex items-center gap-2 font-semibold">
            <CreditCard className="w-5 h-5 text-emerald-700" />
            Mercado Pago
          </div>
          <p className="text-sm text-muted-foreground">
            Pagos en línea (web y WhatsApp). El pedido entra a la bandeja solo cuando el pago se confirma.
          </p>
          {mpDiag ? (
            <div
              className={`text-sm rounded-md border px-3 py-2 ${
                mpDiag.mode === 'production'
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                  : 'bg-amber-50 border-amber-200 text-amber-900'
              }`}
            >
              <strong>Modo actual: {mpDiag.label}</strong>
              {(mpDiag.warnings || []).map((w) => (
                <p key={w} className="text-xs mt-1 opacity-90">
                  {w}
                </p>
              ))}
              {(mpDiag.public_base_url_warnings || []).map((w) => (
                <p key={w} className="text-xs mt-1 font-medium">
                  URL: {w}
                </p>
              ))}
            </div>
          ) : null}
          <div className="flex items-center gap-2">
            <Checkbox
              id="mp_enabled_store"
              checked={Boolean(form.mercadopago_enabled)}
              onCheckedChange={(v) => set('mercadopago_enabled', Boolean(v))}
            />
            <Label htmlFor="mp_enabled_store">Activar Mercado Pago</Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="mp_sandbox_store"
              checked={Boolean(form.mercadopago_sandbox)}
              onCheckedChange={(v) => set('mercadopago_sandbox', Boolean(v))}
            />
            <Label htmlFor="mp_sandbox_store">Modo prueba (sandbox)</Label>
          </div>
          <div>
            <Label htmlFor="mp_token_store">Access Token</Label>
            <Input
              id="mp_token_store"
              type="password"
              autoComplete="off"
              value={form.mercadopago_access_token || ''}
              onChange={(e) => set('mercadopago_access_token', e.target.value)}
              placeholder="APP_USR-…"
              className="font-mono text-sm mt-1"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Si ves •••• al final, déjalo así para no cambiar el token guardado.
            </p>
          </div>
          <div>
            <Label htmlFor="mp_webhook_store">Webhook secret</Label>
            <Input
              id="mp_webhook_store"
              type="password"
              autoComplete="off"
              value={form.mercadopago_webhook_secret || ''}
              onChange={(e) => set('mercadopago_webhook_secret', e.target.value)}
              className="font-mono text-sm mt-1"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Webhook:{' '}
            <code>
              {((form.public_base_url || 'https://tudominio.com').replace(/\/$/, ''))}
              /api/webhooks/mercadopago
            </code>
          </p>
          <div className="text-xs text-muted-foreground rounded-md border p-3 bg-amber-50/80 space-y-2">
            <p className="font-semibold text-amber-900">Error CPT01 o código al pagar en la app</p>
            <ul className="list-disc pl-4 space-y-1 mb-2">
              <li>
                Prueba con <strong>otra cuenta</strong> Mercado Pago (no la del negocio que cobra).
              </li>
              <li>
                Si ves un código como <code>PUMWIRWEKNC0</code>, cópialo y contacta a Soporte Mercado Pago
                (Actividad → detalle del pago).
              </li>
              <li>
                Revisa que «Modo prueba» coincida con el token (<code>TEST-</code> vs <code>APP_USR-</code>).
              </li>
            </ul>
            <p className="font-semibold text-amber-900">Si MP dice «no acepta este medio de pago»</p>
            <ul className="list-disc pl-4 space-y-1">
              <li>
                <strong>Modo prueba activo:</strong> solo tarjetas de prueba de Mercado Pago (no tu tarjeta real).
                Ej. Mastercard <code>5474 9254 3267 0366</code>, CVV <code>123</code>, vence{' '}
                <code>11/30</code>, titular <code>APRO</code> (pago aprobado).
              </li>
              <li>
                <strong>Modo producción:</strong> desactiva «Modo prueba» y usa Access Token de producción (
                <code>APP_USR-…</code> de Credenciales de producción). Ahí sí entran tarjetas reales.
              </li>
              <li>
                El Access Token de <strong>prueba</strong> empieza con <code>TEST-</code>; el de producción con{' '}
                <code>APP_USR-</code>.
              </li>
              <li>
                La cuenta vendedora en Mercado Pago debe tener activados cobros con tarjeta (verificación completada).
              </li>
            </ul>
            <p>
              Documentación:{' '}
              <a
                href="https://www.mercadopago.com.mx/developers/es/docs/checkout-pro/integration-test/test-purchases"
                target="_blank"
                rel="noreferrer"
                className="text-primary underline"
              >
                Tarjetas de prueba (México)
              </a>
            </p>
          </div>
        </div>
        <div>
          <Label>Instrucciones transferencia SPEI</Label>
          <Textarea
            value={form.transfer_bank_instructions || ''}
            onChange={(e) => set('transfer_bank_instructions', e.target.value)}
            placeholder="Banco, CLABE, cuenta…"
          />
          <p className="text-xs text-muted-foreground mt-1">
            En WhatsApp se envía con concepto <strong>PEDIDO #número</strong> (plantilla abajo).
          </p>
        </div>
        <div>
          <Label>Plantilla WhatsApp — transferencia</Label>
          <Textarea
            rows={5}
            value={form.wa_order_transfer_template || ''}
            onChange={(e) => set('wa_order_transfer_template', e.target.value)}
            placeholder="Variables: {store_name}, {order_number}, {total}, {transfer_instructions}"
          />
          <p className="text-xs text-muted-foreground mt-1">Nombre tienda: {waStoreVars}</p>
        </div>
        <div className="rounded-lg border border-border p-4 space-y-2">
          <Label>Motivos de cancelación (cajero)</Label>
          <p className="text-xs text-muted-foreground">
            Una opción por línea. El cajero debe elegir al menos una al cancelar un pedido (puede marcar
            varias).
          </p>
          <Textarea
            rows={5}
            value={(form.order_cancellation_reasons || []).join('\n')}
            onChange={(e) =>
              set(
                'order_cancellation_reasons',
                e.target.value
                  .split('\n')
                  .map((s) => s.trim())
                  .filter(Boolean),
              )
            }
            placeholder={'Cliente canceló\nNo hay ingredientes\nNo llegó la transferencia'}
          />
        </div>
        <div>
          <Label>Plantilla WhatsApp — contra entrega</Label>
          <Textarea
            rows={3}
            value={form.wa_order_cod_template || ''}
            onChange={(e) => set('wa_order_cod_template', e.target.value)}
            placeholder="Variables: {store_name}, {order_number}, {total}, {customer_name}"
          />
        </div>
        <div>
          <Label>Dirección sucursal (pickup)</Label>
          <Input
            value={form.store_pickup_address || ''}
            onChange={(e) => set('store_pickup_address', e.target.value)}
            placeholder="Av. Principal 123, Col. Centro"
          />
        </div>
        <div>
          <Label>URL Google Maps sucursal (pickup)</Label>
          <Input
            value={form.store_pickup_maps_url || ''}
            onChange={(e) => set('store_pickup_maps_url', e.target.value)}
            placeholder="https://maps.google.com/maps?q=..."
          />
        </div>
        <div>
          <Label>Plantilla WhatsApp — pickup confirmado</Label>
          <Textarea
            value={form.wa_pickup_ready_template || ''}
            onChange={(e) => set('wa_pickup_ready_template', e.target.value)}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Variables: {waStoreVars}, {'{customer_name}'}, {'{order_number}'}, {'{pickup_time}'},{' '}
            {'{pickup_maps_url}'}
          </p>
        </div>
        <div>
          <Label>Plantilla WhatsApp — pedido aceptado (domicilio)</Label>
          <Textarea
            rows={4}
            value={form.wa_order_accepted_template || ''}
            onChange={(e) => set('wa_order_accepted_template', e.target.value)}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Variables: {waStoreVars}, {'{customer_name}'}, {'{order_number}'}, {'{eta_time}'}
          </p>
        </div>
        <div>
          <Label>Plantilla WhatsApp — en camino (cliente)</Label>
          <Textarea
            value={form.wa_order_dispatched_template || ''}
            onChange={(e) => set('wa_order_dispatched_template', e.target.value)}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Variables: {waStoreVars}, {'{customer_name}'}, {'{order_number}'}
          </p>
        </div>
        <div>
          <Label>Plantilla WhatsApp — asignación al repartidor</Label>
          <Textarea
            value={form.wa_driver_assignment_template || ''}
            onChange={(e) => set('wa_driver_assignment_template', e.target.value)}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Variables: {waStoreVars}, {'{driver_name}'}, {'{order_number}'}, {'{customer_name}'},{' '}
            {'{customer_phone}'}, {'{total}'}, {'{payment_method_label}'}, {'{payment_method}'},{' '}
            {'{delivery_address}'}, {'{maps_url}'}, {'{lines_summary}'}
          </p>
        </div>
        <div className="rounded-lg border p-4 space-y-3 bg-muted/20">
          <p className="text-sm font-semibold">Experiencia WhatsApp (bot)</p>
          <div>
            <Label>Rango ETA mostrado al cliente (ej. 25–35)</Label>
            <Input
              value={form.wa_eta_display_range || ''}
              onChange={(e) => set('wa_eta_display_range', e.target.value)}
              placeholder="25–35"
            />
          </div>
          <div>
            <Label>Leyenda de tiempo en menú WhatsApp</Label>
            <Textarea
              rows={2}
              value={form.wa_menu_eta_legend_template || ''}
              onChange={(e) => set('wa_menu_eta_legend_template', e.target.value)}
              placeholder="⏱ Tiempo estimado de *{eta_minutes}* minutos ({orders_ahead} pedido(s) en cola)."
            />
            <p className="text-xs text-muted-foreground mt-1">
              Se muestra debajo del saludo. Variables: {'{eta_minutes}'}, {'{orders_ahead}'},{' '}
              {'{orders_ahead_label}'}, {'{eta_range}'}, {'{ready_time}'}. Los minutos se calculan con
              la cola de pedidos en cocina.
            </p>
          </div>
          <div>
            <Label>Descuento por calificar (MXN)</Label>
            <Input
              type="number"
              min={0}
              value={form.wa_rating_discount_amount ?? 30}
              onChange={(e) => set('wa_rating_discount_amount', parseInt(e.target.value, 10) || 0)}
              disabled
            />
            <p className="text-xs text-muted-foreground mt-1">
              Encuesta post-entrega desactivada por el momento.
            </p>
          </div>
        </div>
        <div>
          <Label>Plantilla WhatsApp — pedido entregado (cliente)</Label>
          <Textarea
            rows={8}
            value={form.wa_order_delivered_template || ''}
            onChange={(e) => set('wa_order_delivered_template', e.target.value)}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Encuesta desactivada: se envía un mensaje simple de entrega. Variables activas:{' '}
            {'{customer_name}'}, {'{order_number}'}, {'{store_name}'}.
          </p>
        </div>

        <div className="rounded-lg border-2 border-slate-300 bg-slate-50/80 p-4 space-y-3">
          <p className="text-sm font-semibold flex items-center gap-2">
            <Printer className="w-4 h-4" />
            Ticket de venta (impresión en caja)
          </p>
          <p className="text-sm text-muted-foreground">
            Encabezado del ticket térmico (como ticket de ferretería): imagen arriba y hasta 5 líneas de
            datos del negocio. El cajero imprime desde Pedidos cuando el pedido está entregado o recogido.
          </p>
          <ReceiptTicketImageField
            url={form.receipt_ticket_image_url || ''}
            onChange={(v) => set('receipt_ticket_image_url', v || '')}
          />
          <div>
            <Label>Línea 1 — Nombre del negocio</Label>
            <Input
              value={form.receipt_business_line1 || ''}
              onChange={(e) => set('receipt_business_line1', e.target.value)}
              placeholder={form.wa_brand_name || 'Pizza Lover Club'}
              className="mt-1"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Si está vacío, se usa el nombre comercial de arriba.
            </p>
          </div>
          <div>
            <Label>Línea 2 — Propietario / razón social</Label>
            <Input
              value={form.receipt_business_line2 || ''}
              onChange={(e) => set('receipt_business_line2', e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Línea 3 — RFC</Label>
            <Input
              value={form.receipt_business_line3 || ''}
              onChange={(e) => set('receipt_business_line3', e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Línea 4 — Dirección</Label>
            <Textarea
              rows={2}
              value={form.receipt_business_line4 || ''}
              onChange={(e) => set('receipt_business_line4', e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Línea 5 — Teléfonos</Label>
            <Input
              value={form.receipt_business_line5 || ''}
              onChange={(e) => set('receipt_business_line5', e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Nombre de caja en ticket</Label>
            <Input
              value={form.receipt_register_name || 'Caja 1'}
              onChange={(e) => set('receipt_register_name', e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Pie de ticket — línea 1</Label>
            <Input
              value={form.receipt_footer_line1 || ''}
              onChange={(e) => set('receipt_footer_line1', e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Pie de ticket — línea 2 (opcional)</Label>
            <Input
              value={form.receipt_footer_line2 || ''}
              onChange={(e) => set('receipt_footer_line2', e.target.value)}
              className="mt-1"
            />
          </div>
        </div>

        <div className="rounded-lg border p-4 space-y-3">
          <p className="text-sm font-semibold">Módulos de operación</p>
          <p className="text-xs text-muted-foreground">
            Activa o pausa canales. La pausa de pedidos también está en Caja (toggle en vivo).
          </p>
          {[
            ['accepting_orders', 'Recibiendo pedidos (WhatsApp / web)'],
            ['whatsapp_orders_enabled', 'Pedidos por WhatsApp'],
            ['waiter_orders_enabled', 'Comanda de mesero'],
            ['cashier_enabled', 'Caja'],
            ['online_ordering_enabled', 'Pedidos online (/pedir)'],
            ['kitchen_printing_enabled', 'Impresión térmica cocina'],
            ['show_staff_clock', 'Reloj en panel staff'],
          ].map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={form[key] !== false}
                onCheckedChange={(v) => set(key, Boolean(v))}
              />
              {label}
            </label>
          ))}
          <div className="rounded-md border border-dashed p-3 space-y-2 bg-muted/30">
            <p className="text-sm font-semibold">Comanda → Caja</p>
            <label className="flex items-start gap-2 text-sm">
              <Checkbox
                className="mt-0.5"
                checked={form.comanda_to_cashier_enabled === true}
                onCheckedChange={(v) => set('comanda_to_cashier_enabled', Boolean(v))}
              />
              <span>
                <span className="font-medium block">Comandas a cajero</span>
                <span className="text-xs text-muted-foreground leading-relaxed block mt-0.5">
                  Si está apagado, los pedidos de comanda van solo a cocina; si está encendido, también
                  aparecen en Caja al enviar a cocina.
                </span>
              </span>
            </label>
          </div>
          <div>
            <Label>Mensaje si pedidos en pausa</Label>
            <Textarea
              rows={2}
              className="mt-1"
              value={form.orders_paused_message || ''}
              onChange={(e) => set('orders_paused_message', e.target.value)}
            />
          </div>
        </div>

        <DiningTablesAdminCard />

        <div className="rounded-lg border p-4 space-y-3">
          <p className="text-sm font-semibold flex items-center gap-2">
            <Printer className="w-4 h-4" /> Impresoras térmicas (BLE)
          </p>
          <p className="text-xs text-muted-foreground">
            Empareja desde Cocina (slot cocina) o Caja (slot caja). Requiere Chrome + HTTPS.
          </p>
        </div>

        <Button onClick={save} disabled={saving}>
          Guardar
        </Button>
      </CardContent>
    </Card>
  );
}

function DiningTablesAdminCard() {
  const [tables, setTables] = useState([]);
  const [number, setNumber] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { adminListDiningTables } = await import('../lib/api');
      setTables(await adminListDiningTables());
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    load();
  }, []);

  const add = async () => {
    const n = parseInt(number, 10);
    if (!n) {
      toast.error('Número de mesa inválido');
      return;
    }
    setBusy(true);
    try {
      const { adminCreateDiningTable } = await import('../lib/api');
      await adminCreateDiningTable({ number: n, capacity: 4 });
      setNumber('');
      toast.success(`Mesa ${n} creada`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    setBusy(true);
    try {
      const { adminDeleteDiningTable } = await import('../lib/api');
      await adminDeleteDiningTable(id);
      toast.success('Mesa desactivada');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border p-4 space-y-3">
      <p className="text-sm font-semibold">Mesas del salón</p>
      <div className="flex gap-2 items-end">
        <div>
          <Label>Número</Label>
          <Input
            type="number"
            className="w-24 mt-1"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
          />
        </div>
        <Button type="button" onClick={add} disabled={busy}>
          Agregar mesa
        </Button>
      </div>
      <ul className="text-sm space-y-1">
        {(tables || [])
          .filter((t) => t.is_active !== false)
          .map((t) => (
            <li key={t.id} className="flex justify-between gap-2 border rounded px-2 py-1">
              <span>
                Mesa {t.number} · {t.capacity} pers. · {t.status || 'free'}
              </span>
              <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => remove(t.id)}>
                Quitar
              </Button>
            </li>
          ))}
      </ul>
    </div>
  );
}
